import React, { useState } from 'react';
import { 
  Modal, 
  Button, 
  Table, 
  Tag,
  Typography,
  Space,
  message
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  .sm-modal .ant-modal-content {
    border-radius: 16px !important;
    overflow: hidden !important;
    padding: 0 !important;
    box-shadow: 0 20px 60px rgba(72,93,170,0.18) !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .sm-modal .ant-modal-header {
    background: #485daa !important;
    padding: 16px 24px !important;
    border-bottom: none !important;
    margin-bottom: 0 !important;
  }
  .sm-modal .ant-modal-title {
    color: #fff !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    letter-spacing: -0.2px !important;
  }
  .sm-modal .ant-modal-close {
    color: rgba(255,255,255,0.7) !important;
    top: 14px !important;
  }
  .sm-modal .ant-modal-close:hover {
    color: #fff !important;
    background: rgba(255,255,255,0.15) !important;
    border-radius: 6px !important;
  }
  .sm-modal .ant-modal-body {
    padding: 20px 24px 24px !important;
    background: #f9fafb !important;
  }

  /* Table */
  .sm-table .ant-table {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 13px !important;
    background: transparent !important;
  }
  .sm-table .ant-table-container {
    border-radius: 10px !important;
    overflow: hidden !important;
    border: 1px solid #e5e7eb !important;
    box-shadow: 0 2px 12px rgba(72,93,170,0.07) !important;
  }
  .sm-table .ant-table-thead > tr > th {
    background: #485daa !important;
    color: #fff !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    border-bottom: none !important;
    padding: 10px 12px !important;
    white-space: nowrap !important;
  }
  .sm-table .ant-table-thead > tr > th::before {
    display: none !important;
  }
  .sm-table .ant-table-tbody > tr > td {
    padding: 9px 12px !important;
    border-bottom: 1px solid #f0f0f5 !important;
    color: #111827 !important;
    background: #fff !important;
    transition: background 0.15s !important;
    white-space: nowrap !important;
  }
  .sm-table .ant-table-tbody > tr:nth-child(even) > td {
    background: #f8f9fe !important;
  }
  .sm-table .ant-table-tbody > tr:hover > td {
    background: #eef0f9 !important;
  }
  .sm-table .ant-table-cell-fix-left,
  .sm-table .ant-table-cell-fix-right {
    background: inherit !important;
  }

  /* Row number pill */
  .sm-row-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #eef0f9;
    color: #485daa;
    font-size: 11px;
    font-weight: 600;
    font-family: 'Sora', sans-serif;
  }

  /* Time pill */
  .sm-time-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 6px;
    background: #f1f3fb;
    color: #485daa;
    font-size: 12px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    border: 1px solid rgba(72,93,170,0.15);
  }

  /* Action bar */
  .sm-action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding: 10px 14px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
  }

  /* Meta chips */
  .sm-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }
  .sm-meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    background: #eef0f9;
    color: #485daa;
    border: 1px solid rgba(72,93,170,0.2);
  }

  /* Download btn */
  .sm-dl-btn {
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    height: 36px !important;
    padding: 0 16px !important;
    background: #485daa !important;
    border-color: #485daa !important;
    border-radius: 8px !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #fff !important;
    box-shadow: 0 3px 10px rgba(72,93,170,0.25) !important;
    transition: all 0.2s !important;
  }
  .sm-dl-btn:hover {
    background: #3a4d96 !important;
    border-color: #3a4d96 !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 5px 16px rgba(72,93,170,0.35) !important;
  }
  .sm-dl-btn:disabled {
    background: #d1d5db !important;
    border-color: #d1d5db !important;
    box-shadow: none !important;
    transform: none !important;
  }
`;

const ScheduleModal = ({ isVisible, onClose, popupData, classChangeLogs }) => {
  const { t } = useTranslation();

  const safePopupData = Array.isArray(popupData) ? popupData : [];
  const safeChangeLogs = Array.isArray(classChangeLogs) ? classChangeLogs : [];

  // Sort by date then time
  const sortedData = [...safePopupData].sort((a, b) => {
    const dateTimeA = new Date(`${a.date}T${a.schedule_time || '00:00'}`);
    const dateTimeB = new Date(`${b.date}T${b.schedule_time || '00:00'}`);
    return dateTimeA.getTime() - dateTimeB.getTime();
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: '2-digit'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [h, m] = timeString.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return '0.00 hr';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}.${m.toString().padStart(2, '0')} hr`;
  };

  const getChangeReasonDisplay = (reason) => {
    const map = {
      'admin_adjust':              { color: '#485daa', bg: '#eef0f9', text: 'Admin' },
      'cancelled_by_student':      { color: '#dc2626', bg: '#fef2f2', text: 'CL by S.' },
      'cancelled_by_teacher':      { color: '#ea580c', bg: '#fff7ed', text: 'CL by T.' },
      'class_on_hold_by_student':  { color: '#4f46e5', bg: '#eef2ff', text: 'Hold by S.' },
      'class_on_hold_by_school':   { color: '#16a34a', bg: '#f0fdf4', text: 'Hold by P.' },
      'cancel_same_day':           { color: '#b45309', bg: '#fffbeb', text: 'Same Day CL' },
      'make_up':                   { color: '#7c3aed', bg: '#f5f3ff', text: 'Make Up' },
      'others':                    { color: '#6b7280', bg: '#f9fafb', text: 'Others' },
    };
    return map[reason] || { color: '#6b7280', bg: '#f9fafb', text: reason || '-' };
  };

  const getPlatformLabel = (p) => {
    const map = { online: 'Online', onsite: 'Onsite', athome: 'At Home' };
    return map[p] || p || 'N/A';
  };

  // Derived meta
  const firstRow   = safePopupData[0] || {};
  const classCode  = firstRow.class_code || '-';
  const classType  = firstRow.isprivate ? 'Private' : 'Group';
  const totalHours = firstRow.total_hours || 0;
  const platform   = getPlatformLabel(firstRow.platform);

  // PDF
  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    loadSarabunFont(doc);

    const margin = 15;
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;

    const headerImage = new Image();
    headerImage.src = '/images/plann_logo.png';

    headerImage.onload = () => {
      const imgAspectRatio = headerImage.width / headerImage.height;
      const targetHeight = 18;
      const targetWidth  = targetHeight * imgAspectRatio;

      doc.addImage(headerImage, 'PNG', centerX - targetWidth / 2, margin, targetWidth, targetHeight);

      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(72, 93, 170);
      doc.text('Schedule', centerX, margin + targetHeight + 8, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`${classCode}  •  ${classType}  •  ${platform}  •  Total: ${formatMinutesToHours(totalHours)}`, centerX, margin + targetHeight + 15, { align: 'center' });

      let currentY = margin + targetHeight + 25;

      const columns = [
        { header: 'No.',        dataKey: 'index' },
        { header: t('day'),     dataKey: 'day' },
        { header: t('date'),    dataKey: 'date' },
        { header: t('time'),    dataKey: 'time' },
        { header: t('hours'),   dataKey: 'hours' },
        { header: t('teacher'), dataKey: 'nickname' },
        { header: t('subject'), dataKey: 'subject' },
        { header: t('classchange'), dataKey: 'reason' },
      ];

      const dayMap = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };

      (doc as any).autoTable({
        startY: currentY,
        head: [columns.map(c => c.header)],
        body: sortedData.map((row, i) => {
          const changeLog = safeChangeLogs.find(ccl => ccl.classid === row.classid);
          const reason = changeLog ? getChangeReasonDisplay(changeLog.changereason).text : '-';
          const safe = row.schedule_hour ? String(row.schedule_hour) : '';
          let hours = '0.00 hr';
          const match = safe.match(/(\d+)h\s*(\d+)m/);
          if (match) hours = `${match[1]}.${match[2].padStart(2,'0')} hr`;
          else if (!isNaN(Number(safe)) && safe) {
            const mins = Number(safe);
            hours = `${Math.floor(mins/60)}.${String(mins%60).padStart(2,'0')} hr`;
          }

          return [
            i + 1,
            dayMap[row.schedule_day] || (row.schedule_day || '').substring(0, 3),
            formatDate(row.date),
            `${formatTime(row.schedule_time)} - ${formatTime(row.end_time)}`,
            hours,
            row.nickname || '-',
            row.subject_name || '-',
            reason,
          ];
        }),
        theme: 'grid',
        styles: { font: 'Sarabun', fontSize: 9, textColor: [51,51,51], lineColor: [220,220,230] },
        headStyles: { fillColor: [72,93,170], textColor: [255,255,255], fontStyle: 'bold', fontSize: 10 },
        alternateRowStyles: { fillColor: [248,249,254] },
        margin: { top: 10, bottom: 25, left: margin, right: margin },
        pageBreak: 'auto',
        showHead: 'everyPage',
      });

      // Agreement
      const finalY = (doc as any).lastAutoTable.finalY || currentY + 100;
      let agY = finalY + 15;
      if (agY > pageHeight - 80) { doc.addPage(); agY = margin + 20; }

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(72,93,170);
      doc.text('Agreement:', margin, agY);

      const agreementLines = [
        'Class cancellation should be made 48 hours before class meeting, or the unexpected cancelled',
        'hours will be deducted for a course teacher. Schedule change can be made no more than 3 times.',
        'Student and/or parent or guardian shall be responsible for any damage or injury which he/she causes to',
        'persons or PLANN property. I, therefore, agree to these terms and agreements.',
        'Registered lesson hours are valid for 2 years (24 months) and will automatically expire upon the end of this period.'
      ];

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80,80,80);
      agreementLines.forEach((line, idx) => {
        doc.text(line, margin, agY + 6 + idx * 4.5);
      });

      const sigY = Math.min(agY + 6 + agreementLines.length * 4.5 + 14, pageHeight - 35);
      const sigLen = 55;
      [
        { label: 'Student',        x: margin },
        { label: 'Course Teacher', x: centerX - sigLen / 2 },
        { label: 'Head Teacher',   x: pageWidth - margin - sigLen },
      ].forEach(({ label, x }) => {
        doc.setFontSize(9);
        doc.setTextColor(80,80,80);
        doc.text(label, x, sigY);
        doc.setDrawColor(180,180,200);
        doc.setLineWidth(0.4);
        doc.line(x, sigY + 2, x + sigLen, sigY + 2);
      });

      // Footer
      const total = (doc.internal as any).getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(160,160,170);
        doc.text(`Page ${i} of ${total}`, centerX, pageHeight - 14, { align: 'center' });
        doc.text(`Printed on ${new Date().toLocaleDateString('en-GB')}`, centerX, pageHeight - 8, { align: 'center' });
      }

      doc.save(`${classCode}_schedule.pdf`);
      message.success('PDF downloaded!');
    };

    headerImage.onerror = () => message.error('Failed to load logo');
  };

  // Table columns
  const columns = [
    {
      title: 'No.',
      key: 'index',
      width: 50,
      render: (_, __, i) => <span className="sm-row-num">{i + 1}</span>,
    },
    {
      title: t('day'),
      dataIndex: 'schedule_day',
      key: 'day',
      width: 72,
      render: (day) => {
        const map = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
        return <span style={{ fontWeight: 600, color: '#485daa', fontSize: 12 }}>{map[day] || day || '-'}</span>;
      },
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 90,
      render: (d) => <span style={{ color: '#374151', fontSize: 12 }}>{formatDate(d)}</span>,
    },
    {
      title: t('time'),
      key: 'time',
      width: 120,
      render: (_, r) => (
        <span className="sm-time-pill">
          {formatTime(r.schedule_time)} – {formatTime(r.end_time)}
        </span>
      ),
    },
    {
      title: t('hours'),
      dataIndex: 'schedule_hour',
      key: 'hours',
      width: 72,
      render: (v) => {
        const s = v ? String(v) : '';
        const m = s.match(/(\d+)h\s*(\d+)m/);
        const label = m ? `${m[1]}.${m[2].padStart(2,'0')}h` : (s || '0h');
        return <span style={{ color: '#6b7280', fontSize: 12 }}>{label}</span>;
      },
    },
    {
      title: t('teacherName'),
      dataIndex: 'nickname',
      key: 'teacher',
      render: (v) => <span style={{ fontWeight: 500 }}>{v || '-'}</span>,
    },
    {
      title: t('subjectName'),
      dataIndex: 'subject_name',
      key: 'subject',
      render: (v) => <span style={{ color: '#374151' }}>{v || '-'}</span>,
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 80,
      render: (p) => {
        const label = getPlatformLabel(p);
        const colorMap = { Online: '#485daa', Onsite: '#16a34a', 'At Home': '#d97706' };
        const bgMap   = { Online: '#eef0f9', Onsite: '#f0fdf4', 'At Home': '#fffbeb' };
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            color: colorMap[label] || '#6b7280',
            background: bgMap[label] || '#f3f4f6',
          }}>
            {label}
          </span>
        );
      },
    },
    {
      title: t('classchange'),
      key: 'reason',
      width: 100,
      render: (_, r) => {
        const cl = safeChangeLogs.find(c => c.classid === r.classid);
        const disp = cl ? getChangeReasonDisplay(cl.changereason) : null;
        if (!disp) return <span style={{ color: '#d1d5db' }}>—</span>;
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            color: disp.color,
            background: disp.bg,
          }}>
            {disp.text}
          </span>
        );
      },
    },
    {
      title: t('note'),
      key: 'note',
      render: (_, r) => {
        const cl = safeChangeLogs.find(c => c.classid === r.classid);
        return <span style={{ color: '#9ca3af', fontSize: 12 }}>{cl?.note || '—'}</span>;
      },
    },
  ];

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        className="sm-modal"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src="/images/plann_logo.png"
              alt="logo"
              style={{ height: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
            />
            <span style={{ opacity: 0.4, fontSize: 14 }}>|</span>
            <span>{classCode}</span>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.2)',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 0.3,
            }}>
              {classType}
            </span>
          </div>
        }
        width={1200}
      >
        {/* Action bar */}
        <div className="sm-action-bar">
          <div className="sm-meta">
            <span className="sm-meta-chip">
              🕐 {formatMinutesToHours(totalHours)}
            </span>
            <span className="sm-meta-chip">
              📍 {platform}
            </span>
            <span className="sm-meta-chip">
              📅 {sortedData.length} sessions
            </span>
          </div>

          <Button
            className="sm-dl-btn"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>
        </div>

        {/* Schedule Table */}
        <Table
          className="sm-table"
          dataSource={sortedData}
          columns={columns}
          rowKey={(r) => r.id || r.classid}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </>
  );
};

export default ScheduleModal;